import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { LayoutDashboard, MessageSquare, BookOpen, LogOut, User, Shield, CreditCard, Settings, GraduationCap, PenLine, Target, Briefcase, FileText, Phone, FolderOpen, Brain, BarChart3 } from "lucide-react";
import { SpurwechselLogo } from "@/components/SpurwechselLogo";

const navItems = [
  { path: "/lernpfad", label: "Lernpfad", icon: Target },
  { path: "/conversation", label: "Gespräch", icon: Phone },
  { path: "/dashboard", label: "Übersicht", icon: LayoutDashboard },
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const initials = user?.name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?";

  return (
    <div className="min-h-screen bg-background">
      <nav className="glass-nav sticky top-0 z-50" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <Link to="/dashboard" className="flex items-center gap-2" data-testid="nav-logo">
                <SpurwechselLogo size="small" />
              </Link>
              <div className="hidden md:flex items-center gap-1">
                {navItems.map(item => {
                  const Icon = item.icon;
                  const active = location.pathname.startsWith(item.path.split("/new")[0]);
                  return (
                    <Link key={item.path} to={item.path} data-testid={`nav-${item.label.toLowerCase()}`}>
                      <Button
                        variant={active ? "secondary" : "ghost"}
                        size="sm"
                        className={`gap-2 font-body text-sm ${active ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                      >
                        <Icon className="w-4 h-4" />
                        {item.label}
                      </Button>
                    </Link>
                  );
                })}
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 px-2" data-testid="user-menu-trigger">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs font-body">{initials}</AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline font-body text-sm">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem className="font-body text-sm text-muted-foreground" disabled>
                  {user?.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/akte")} className="font-body text-sm cursor-pointer" data-testid="akte-link">
                  <FileText className="w-4 h-4 mr-2" />
                  MPU-Akte
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/documents")} className="font-body text-sm cursor-pointer" data-testid="documents-link">
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Dokumente
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/knowledge")} className="font-body text-sm cursor-pointer" data-testid="knowledge-link">
                  <Brain className="w-4 h-4 mr-2" />
                  Wissen & Beispiele
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/readiness")} className="font-body text-sm cursor-pointer" data-testid="readiness-link">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Übungsstand
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} className="font-body text-sm cursor-pointer" data-testid="profile-link">
                  <Settings className="w-4 h-4 mr-2" />
                  Einstellungen
                </DropdownMenuItem>
                {user?.is_admin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} className="font-body text-sm cursor-pointer" data-testid="admin-link">
                    <Shield className="w-4 h-4 mr-2" />
                    Admin
                  </DropdownMenuItem>
                )}
                {(user?.is_expert || user?.is_admin) && (
                  <DropdownMenuItem onClick={() => navigate("/expert")} className="font-body text-sm cursor-pointer" data-testid="expert-link">
                    <Briefcase className="w-4 h-4 mr-2" />
                    Experten-Bereich
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="font-body text-sm cursor-pointer" data-testid="logout-button">
                  <LogOut className="w-4 h-4 mr-2" />
                  Abmelden
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {/* Mobile nav */}
        <div className="md:hidden border-t border-border/50 px-2 py-1.5 flex gap-1 overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}>
          {navItems.map(item => {
            const Icon = item.icon;
            const active = location.pathname.startsWith(item.path.split("/new")[0]);
            return (
              <Link key={item.path} to={item.path} className="flex-shrink-0">
                <Button variant={active ? "secondary" : "ghost"} size="sm" className="gap-1 text-xs font-body px-2.5 h-8">
                  <Icon className="w-3.5 h-3.5" />
                  {item.label}
                </Button>
              </Link>
            );
          })}
        </div>
      </nav>
      <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${location.pathname.startsWith("/conversation") ? "py-2" : "py-8"}`}>
        {children}
      </main>
    </div>
  );
}
